import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Package, UserCheck, History, X, Filter } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// 2026-05-25 — On large sites the original list view auto-loaded
// every plan with floor stock AND ran the per-plan `_floor_summary`
// fan-out. That made page-open painfully slow. New UX: load only a
// lightweight index of plans (date-filterable) for the dropdown,
// and hydrate ONE plan's details on selection. Initial render does
// not call the heavy `/plans` endpoint at all.

export default function ComboV2DistributorPlanList() {
  const navigate = useNavigate();
  // Lightweight dropdown index — populated from `/plans-index`.
  const [planIndex, setPlanIndex] = useState([]);
  const [indexLoading, setIndexLoading] = useState(false);
  // Date filter — defaults to LAST 7 DAYS INCLUDING TODAY.
  // 2026-05-25 — Operators reported the dropdown was overwhelming
  // when defaulting to "all time"; a rolling 7-day window matches
  // their daily workflow (most active plans are <1 week old).
  const _todayIso = () => new Date().toISOString().slice(0, 10);
  const _daysAgoIso = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };
  const [dateFrom, setDateFrom] = useState(_daysAgoIso(6));  // 6 days back + today = 7 days
  const [dateTo, setDateTo] = useState(_todayIso());
  // Selected plan + its hydrated full detail.
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  // Dialog state — which plan's bundles are being viewed.
  const [bundlePlan, setBundlePlan] = useState(null);
  // History drawer state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPlans, setHistoryPlans] = useState([]);
  const [historyOnlyComplete, setHistoryOnlyComplete] = useState(true);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  // Dropdown open-state for the searchable combobox.
  const [planPickerOpen, setPlanPickerOpen] = useState(false);

  // Build a quick lookup so we can show status badge next to plan
  // in the dropdown without a re-fetch.
  const planIndexById = useMemo(() => {
    const m = {};
    for (const p of planIndex) m[p.plan_id] = p;
    return m;
  }, [planIndex]);

  const loadIndex = async () => {
    try {
      setIndexLoading(true);
      const tk = localStorage.getItem('session_token');
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/combo-v2/distributor/plans-index`, {
        headers: { Authorization: `Bearer ${tk}` },
        params,
      });
      setPlanIndex(r.data.plans || []);
    } catch (e) {
      toast.error('Failed to load plan index');
    } finally { setIndexLoading(false); }
  };

  const loadPlanDetail = async (planId) => {
    if (!planId) {
      setPlans([]);
      return;
    }
    try {
      setLoading(true);
      const tk = localStorage.getItem('session_token');
      const r = await axios.get(`${API}/api/combo-v2/distributor/plans`, {
        headers: { Authorization: `Bearer ${tk}` },
        params: { plan_id: planId },
      });
      setPlans(r.data.plans || []);
    } catch (e) {
      toast.error('Failed to load plan');
    } finally { setLoading(false); }
  };

  const loadHistory = async (onlyComplete) => {
    try {
      setHistoryLoading(true);
      const tk = localStorage.getItem('session_token');
      const params = { limit: 1000 };
      if (onlyComplete) params.status = 'COMPLETED';
      if (historyDateFrom) params.date_from = historyDateFrom;
      if (historyDateTo) params.date_to = historyDateTo;
      const r = await axios.get(`${API}/api/combo-v2/distributor/plans/history`, {
        headers: { Authorization: `Bearer ${tk}` },
        params,
      });
      setHistoryPlans(r.data.plans || []);
    } catch (e) {
      toast.error('Failed to load history');
    } finally { setHistoryLoading(false); }
  };

  // Load lightweight index on mount with the default 7-day window.
  // From here on, the operator must hit "Search" to re-run; this
  // avoids a network request per keystroke when adjusting the
  // date inputs.
  useEffect(() => { loadIndex(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const closeBundleDialog = () => setBundlePlan(null);

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="distributor-list-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/wms/combo-v2')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-3xl font-bold text-stone-900 flex-1">Piece Distributor</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setHistoryOpen(true); loadHistory(historyOnlyComplete); }}
            data-testid="distributor-history-btn"
          >
            <History className="h-4 w-4 mr-1" /> History
          </Button>
        </div>

        {/* Filter bar — date range + plan dropdown.
            2026-05-25: the table below shows nothing until a plan is
            picked, so opening this page is instantaneous regardless
            of how many plans exist. */}
        <Card className="p-4 mb-4 bg-white border-stone-200">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-stone-500" />
            <span className="text-xs text-stone-600 font-medium">Filter:</span>
            <div className="flex items-center gap-1">
              <label className="text-xs text-stone-500">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border rounded px-2 py-1 text-xs"
                data-testid="distributor-filter-date-from"
              />
            </div>
            <div className="flex items-center gap-1">
              <label className="text-xs text-stone-500">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border rounded px-2 py-1 text-xs"
                data-testid="distributor-filter-date-to"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                data-testid="distributor-filter-date-clear"
              >
                Clear dates
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => {
                // Explicit fetch — clears selection so the operator
                // doesn't keep stale plan-detail from a previous
                // window.
                setSelectedPlanId('');
                setPlans([]);
                loadIndex();
              }}
              disabled={indexLoading}
              data-testid="distributor-filter-search"
            >
              {indexLoading ? 'Searching…' : 'Search'}
            </Button>
            <div className="flex-1 min-w-[260px]">
              {/* Searchable combobox — operator can type plan_id
                  or plan_name to narrow the list. Built from shadcn
                  Command + Popover so we keep the same look-and-feel
                  as the rest of the WMS but get keyboard-driven
                  filter & arrow-key navigation. */}
              <Popover open={planPickerOpen} onOpenChange={setPlanPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={planPickerOpen}
                    className="w-full justify-between font-normal"
                    data-testid="distributor-plan-select"
                  >
                    {(() => {
                      const sel = planIndexById[selectedPlanId];
                      if (!sel) {
                        return (
                          <span className="text-stone-500">
                            {indexLoading
                              ? 'Loading plans…'
                              : planIndex.length === 0
                                ? 'No plans in selected date range'
                                : `Select a plan (${planIndex.length} available)`}
                          </span>
                        );
                      }
                      return (
                        <span className="flex items-center gap-2 truncate">
                          <span className="font-mono text-xs">{sel.plan_id}</span>
                          <span className="truncate">— {sel.plan_name || '(no name)'}</span>
                          <Badge
                            variant={sel.status === 'COMPLETED' ? 'default' : 'outline'}
                            className="text-[10px]"
                          >
                            {sel.status || 'UNKNOWN'}
                          </Badge>
                        </span>
                      );
                    })()}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 w-[--radix-popover-trigger-width] max-w-[640px]"
                  align="start"
                  data-testid="distributor-plan-picker-content"
                >
                  <Command
                    filter={(value, search) => {
                      // `value` is the lower-cased plan_id from the
                      // CommandItem. Match against the FULL row data
                      // (id + name + status) so the operator can
                      // search by any field. cmdk default-lowercases
                      // both sides for us.
                      const p = planIndexById[value];
                      if (!p) return 0;
                      const haystack = (
                        (p.plan_id || '') + ' ' +
                        (p.plan_name || '') + ' ' +
                        (p.status || '')
                      ).toLowerCase();
                      return haystack.includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput
                      placeholder="Search plan id, name, or status…"
                      data-testid="distributor-plan-picker-search"
                    />
                    <CommandList className="max-h-[360px]">
                      <CommandEmpty>No plans match.</CommandEmpty>
                      <CommandGroup>
                        {planIndex.map((p) => (
                          <CommandItem
                            key={p.plan_id}
                            value={p.plan_id}
                            onSelect={(v) => {
                              setSelectedPlanId(v);
                              setPlanPickerOpen(false);
                              loadPlanDetail(v);
                            }}
                            data-testid={`distributor-plan-option-${p.plan_id}`}
                          >
                            <Check
                              className={
                                'mr-2 h-4 w-4 ' +
                                (selectedPlanId === p.plan_id
                                  ? 'opacity-100'
                                  : 'opacity-0')
                              }
                            />
                            <span className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="font-mono text-xs">{p.plan_id}</span>
                              <span className="text-stone-700 truncate">
                                — {p.plan_name || '(no name)'}
                              </span>
                              <Badge
                                variant={p.status === 'COMPLETED' ? 'default' : 'outline'}
                                className="text-[10px] ml-auto"
                              >
                                {p.status || 'UNKNOWN'}
                              </Badge>
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {selectedPlanId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSelectedPlanId(''); setPlans([]); }}
                data-testid="distributor-plan-clear"
              >
                Clear plan
              </Button>
            )}
          </div>
        </Card>

        <Card>
          {!selectedPlanId ? (
            <div className="p-12 text-center text-stone-500" data-testid="distributor-empty-state">
              Pick a plan from the dropdown above to view its floor stock and bundles.
            </div>
          ) : loading ? (
            <div className="p-12 text-center text-stone-500"><Loader2 className="h-6 w-6 animate-spin inline mr-2"/> Loading…</div>
          ) : !plans.length ? (
            <div className="p-12 text-center text-stone-500">This plan has no floor stock yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="dist-plans-table">
              <thead className="bg-stone-100">
                <tr>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3">Floor Pcs</th>
                  <th className="text-right p-3">Bundles</th>
                  <th className="text-right p-3">Sessions</th>
                  <th className="text-center p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => {
                  const bundles = p.floor_bundles || [];
                  const bundleCount = p.floor_bundle_count ?? bundles.length;
                  const pickedCount = p.plan_picked_bundles ?? bundleCount;
                  const totalCount = p.plan_total_bundles ?? pickedCount;
                  return (
                    <tr key={p.plan_id} className="border-t hover:bg-stone-50">
                      <td className="p-3 font-mono text-xs">{p.plan_id}</td>
                      <td className="p-3">{p.plan_name}</td>
                      <td className="p-3 text-center"><Badge>{p.status}</Badge></td>
                      <td className="p-3 text-right font-bold">{p.floor_total_pcs}</td>
                      <td className="p-3 text-right">
                        {bundleCount === 0 ? (
                          <span className="inline-flex items-center gap-1 text-stone-400">
                            <Package className="w-3.5 h-3.5" />
                            <span data-testid={`bundles-count-${p.plan_id}`}>
                              {pickedCount}<span className="text-stone-300"> / {totalCount}</span>
                            </span>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setBundlePlan(p)}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 hover:underline font-medium"
                            data-testid={`bundles-link-${p.plan_id}`}
                            aria-label={`View ${pickedCount} picked of ${totalCount} bundle(s) for plan ${p.plan_name || p.plan_id}`}
                          >
                            <Package className="w-3.5 h-3.5" />
                            <span data-testid={`bundles-count-${p.plan_id}`}>
                              {pickedCount}
                              <span className="text-stone-400 font-normal"> / {totalCount}</span>
                            </span>
                          </button>
                        )}
                      </td>
                      <td className="p-3 text-right">{p.sessions_count}</td>
                      <td className="p-3 text-center">
                        <Button size="sm" onClick={() => navigate(`/wms/combo-v2/distributor/${p.plan_id}`)} data-testid={`open-${p.plan_id}`}>
                          <UserCheck className="h-4 w-4 mr-1" /> Open
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Dialog open={!!bundlePlan} onOpenChange={(o) => { if (!o) closeBundleDialog(); }}>
        <DialogContent
          className="max-w-3xl"
          data-testid={bundlePlan ? `floor-bundles-dialog-${bundlePlan.plan_id}` : 'floor-bundles-dialog'}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-700" />
              Plan Bundles · {bundlePlan?.plan_name || bundlePlan?.plan_id}
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const picked = bundlePlan?.plan_picked_bundles ?? 0;
                const total = bundlePlan?.plan_total_bundles ?? 0;
                const unpicked = bundlePlan?.unpicked_bundles_count ?? Math.max(0, total - picked);
                return (
                  <span>
                    <strong>{picked}</strong> picked · <strong>{unpicked}</strong> not yet picked ·
                    {' '}<strong>{total}</strong> total bundle(s) on this plan.
                    Floor pieces: <strong>{bundlePlan?.floor_total_pcs ?? 0}</strong>.
                  </span>
                );
              })()}
              {(bundlePlan?.picked_not_handed_over_count ?? 0) > 0 && (
                <span className="block mt-1 text-amber-700">
                  ⚠ {bundlePlan.picked_not_handed_over_count} bundle(s) marked picked on slip but
                  <strong> NOT yet handed over</strong> to distributor — see "Picked but not handed over" section below.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="text-xs font-semibold text-stone-700 px-1 pt-1" data-testid="floor-bundles-section-title">
            On distributor floor ({(bundlePlan?.floor_bundles || []).length})
          </div>
          <div className="rounded-lg border border-stone-200 max-h-[28vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Bundle Code</th>
                  <th className="text-left px-3 py-2">SKU</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Picker</th>
                  <th className="text-right px-3 py-2">Available Pcs</th>
                </tr>
              </thead>
              <tbody>
                {(bundlePlan?.floor_bundles || []).length === 0 && (
                  <tr><td colSpan={6} className="text-center text-stone-400 py-3">No bundles on floor.</td></tr>
                )}
                {(bundlePlan?.floor_bundles || []).map((b, idx) => (
                  <tr
                    key={b.bundle_code}
                    className="border-t border-stone-100"
                    data-testid={`floor-bundle-${b.bundle_code}`}
                  >
                    <td className="px-3 py-1.5 text-stone-500">{idx + 1}</td>
                    <td className="px-3 py-1.5 font-mono text-stone-800">{b.bundle_code}</td>
                    <td className="px-3 py-1.5 text-stone-600">{b.sku_code || '—'}</td>
                    <td className="px-3 py-1.5">
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                        Picked
                      </Badge>
                    </td>
                    <td
                      className="px-3 py-1.5 text-stone-700"
                      data-testid={`floor-bundle-picker-${b.bundle_code}`}
                    >
                      {b.picked_by_name || <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right font-semibold text-stone-800">
                      {b.available_qty}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(bundlePlan?.unpicked_bundles_count ?? 0) > 0 && (
            <>
              <div className="text-xs font-semibold text-stone-700 px-1 pt-3" data-testid="unpicked-bundles-section-title">
                Not yet picked ({bundlePlan.unpicked_bundles_count})
              </div>
              <div className="rounded-lg border border-stone-200 bg-stone-50/40 max-h-[28vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-100 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Bundle Code</th>
                      <th className="text-left px-3 py-2">SKU</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Bin</th>
                      <th className="text-left px-3 py-2">Assigned Picker</th>
                      <th className="text-right px-3 py-2">Req. Pcs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bundlePlan?.unpicked_bundles || []).map((b, idx) => (
                      <tr
                        key={b.bundle_code}
                        className="border-t border-stone-100"
                        data-testid={`unpicked-bundle-${b.bundle_code}`}
                      >
                        <td className="px-3 py-1.5 text-stone-500">{idx + 1}</td>
                        <td className="px-3 py-1.5 font-mono text-stone-800">{b.bundle_code}</td>
                        <td className="px-3 py-1.5 text-stone-600">{b.sku_code || '—'}</td>
                        <td className="px-3 py-1.5">
                          <Badge className="bg-stone-200 text-stone-700 hover:bg-stone-200">
                            Not Picked
                          </Badge>
                        </td>
                        <td className="px-3 py-1.5 text-stone-600 font-mono text-xs">
                          {b.bin_location || '—'}
                        </td>
                        <td className="px-3 py-1.5 text-stone-700">
                          {b.assigned_picker_name || <span className="text-stone-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-stone-800">
                          {b.required_qty || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {(bundlePlan?.picked_not_handed_over_count ?? 0) > 0 && (
            <>
              <div className="text-xs font-semibold text-amber-800 px-1 pt-3" data-testid="not-handed-over-section-title">
                Picked but not handed over ({bundlePlan.picked_not_handed_over_count})
              </div>
              <div className="rounded-lg border border-amber-300 bg-amber-50/30 max-h-[28vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-amber-100 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Bundle Code</th>
                      <th className="text-left px-3 py-2">SKU</th>
                      <th className="text-left px-3 py-2">Picker</th>
                      <th className="text-left px-3 py-2">Current Status</th>
                      <th className="text-left px-3 py-2">Where</th>
                      <th className="text-right px-3 py-2">Avail Pcs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(bundlePlan?.picked_not_handed_over || []).map((b, idx) => (
                      <tr
                        key={b.bundle_code}
                        className="border-t border-amber-200"
                        data-testid={`not-handed-over-bundle-${b.bundle_code}`}
                      >
                        <td className="px-3 py-1.5 text-amber-700">{idx + 1}</td>
                        <td className="px-3 py-1.5 font-mono text-amber-900">{b.bundle_code}</td>
                        <td className="px-3 py-1.5 text-amber-800">{b.sku_code || '—'}</td>
                        <td className="px-3 py-1.5 text-amber-800">
                          {b.picked_by_name || <span className="text-amber-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-amber-900 font-semibold">
                          {b.current_status}
                        </td>
                        <td className="px-3 py-1.5 text-amber-700 text-xs">
                          {b.current_section || ''}{b.current_bin ? ` · ${b.current_bin}` : ''}
                          {b.current_plan_tag && b.current_plan_tag !== bundlePlan.plan_id
                            ? ` · plan ${b.current_plan_tag}` : ''}
                        </td>
                        <td className="px-3 py-1.5 text-right font-semibold text-amber-900">
                          {b.available_qty}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={closeBundleDialog}
              data-testid="floor-bundles-dialog-close"
            >
              Close
            </Button>
            {bundlePlan && (
              <Button
                onClick={() => {
                  navigate(`/wms/combo-v2/distributor/${bundlePlan.plan_id}`);
                  closeBundleDialog();
                }}
                data-testid="floor-bundles-dialog-open-plan"
              >
                <UserCheck className="h-4 w-4 mr-1" /> Open Plan
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* History drawer — plans previously worked on by the distributor */}
      {historyOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40"
          onClick={() => setHistoryOpen(false)}
        >
          <div
            className="bg-white w-full max-w-3xl h-full overflow-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="distributor-history-drawer"
          >
            <div className="sticky top-0 bg-white border-b border-stone-200 p-4 flex items-center justify-between z-10">
              <div>
                <div className="font-semibold text-stone-900 flex items-center gap-2">
                  <History className="h-4 w-4" /> Piece Distributor History
                </div>
                <div className="text-xs text-stone-500">
                  Plans the distributor has previously worked on — newest first.
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="date"
                  value={historyDateFrom}
                  onChange={(e) => setHistoryDateFrom(e.target.value)}
                  className="border rounded px-2 py-1 text-xs"
                  data-testid="history-filter-date-from"
                  placeholder="From"
                />
                <input
                  type="date"
                  value={historyDateTo}
                  onChange={(e) => setHistoryDateTo(e.target.value)}
                  className="border rounded px-2 py-1 text-xs"
                  data-testid="history-filter-date-to"
                  placeholder="To"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadHistory(historyOnlyComplete)}
                  data-testid="history-filter-apply"
                >
                  Apply
                </Button>
                <Button
                  size="sm"
                  variant={historyOnlyComplete ? 'default' : 'outline'}
                  onClick={() => { setHistoryOnlyComplete(true); loadHistory(true); }}
                  data-testid="history-filter-completed"
                >
                  Completed
                </Button>
                <Button
                  size="sm"
                  variant={!historyOnlyComplete ? 'default' : 'outline'}
                  onClick={() => { setHistoryOnlyComplete(false); loadHistory(false); }}
                  data-testid="history-filter-all"
                >
                  All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setHistoryOpen(false)}
                  data-testid="history-drawer-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="p-4">
              {historyLoading ? (
                <div className="py-10 text-center text-stone-500">
                  <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
              ) : !historyPlans.length ? (
                <div className="py-10 text-center text-stone-500" data-testid="history-empty">
                  No history yet.
                </div>
              ) : (
                <table className="w-full text-xs" data-testid="history-table">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left p-1.5">Plan</th>
                      <th className="text-left p-1.5">Name</th>
                      <th className="text-right p-1.5">Handovers</th>
                      <th className="text-right p-1.5">Qty Scanned</th>
                      <th className="text-left p-1.5">PP Staff</th>
                      <th className="text-left p-1.5">Last Activity</th>
                      <th className="text-center p-1.5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyPlans.map((hp) => {
                      const staffLabels = (hp.pp_staff || [])
                        .map((s) => s.staff_name)
                        .slice(0, 3)
                        .join(', ');
                      const more = (hp.pp_staff || []).length - 3;
                      const lastStr = hp.last_activity_at
                        ? new Date(hp.last_activity_at).toLocaleString()
                        : '—';
                      return (
                        <tr key={hp.plan_id} className="border-t border-stone-200" data-testid={`history-row-${hp.plan_id}`}>
                          <td className="p-1.5 font-mono">{hp.plan_id}</td>
                          <td className="p-1.5">{hp.plan_name || '—'}</td>
                          <td className="p-1.5 text-right">
                            <span className="font-semibold">{hp.total_handovers}</span>
                            <span className="text-[10px] text-stone-500 ml-1">
                              ({hp.completed_handovers}✓{hp.active_handovers > 0 ? `, ${hp.active_handovers}•` : ''})
                            </span>
                          </td>
                          <td className="p-1.5 text-right">
                            {hp.total_qty_scanned} / {hp.total_qty_assigned}
                          </td>
                          <td className="p-1.5">
                            {staffLabels}{more > 0 ? ` +${more}` : ''}
                          </td>
                          <td className="p-1.5">{lastStr}</td>
                          <td className="p-1.5 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigate(`/wms/combo-v2/distributor/${hp.plan_id}`);
                                setHistoryOpen(false);
                              }}
                              data-testid={`history-open-${hp.plan_id}`}
                            >
                              Open
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
